// src/index.js
import * as Sentry from "@sentry/react";

Sentry.init({
    dsn: "https://938ee4b92dcf0e4743bca68b4f574af7@o4510998040543232.ingest.us.sentry.io/4510998042705920",
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { ClerkProvider } from '@clerk/clerk-react';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { getCachedSiteContent } from './services/firebase';
import { applyThemeFromSiteContent } from './styles/theme';

// ── Pre-React theme application ──
// Read the localStorage-cached siteConfig (if any) and apply admin's theme
// overrides to `document.documentElement` BEFORE the React tree first
// renders. Without this, returning visitors see a brief flash of default
// brand colors before the App.js useEffect runs and the async siteConfig
// fetch resolves (~200-500ms typically — visible as "weird colored page").
//
// First-ever visitors (cache miss) still see defaults briefly — that's
// unavoidable without SSR. After their first visit, the cache persists and
// every subsequent paint is correct from frame zero.
//
// This is sync DOM mutation against `document.documentElement`, executed
// during the JS bundle's top-level script phase — runs before
// `root.render()` schedules React's first commit.
try {
    const cachedSiteContent = getCachedSiteContent();
    if (cachedSiteContent) applyThemeFromSiteContent(cachedSiteContent);
} catch { /* fail open — App.js useEffect will still apply the theme post-fetch */ }

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Global Clerk appearance — matches the app's brand pink CTAs (sign up,
// subscribe, "See Workouts", etc.) so the auth flows feel like part of the
// same product surface, not a generic Clerk-default form.
//
// Color choices are explicitly tuned for legibility (WCAG AA Normal, 4.5:1+
// on white) since these flows are how new users — including older
// subscribers — first interact with the platform. The previous values
// used a pale-pink colorPrimary (#f0d6d4) for footer action links, which
// rendered at ~1.3:1 contrast — essentially invisible. Now we use the
// actual brand magenta-pink (#c33764) and its darker counterpart
// (#a02a52) for accents/links/buttons, plus a darker secondary gray
// (#6b6660 vs the previous #8a837d) for labels and subtitles.
//
// These values are hardcoded rather than referencing CSS variables because
// ClerkProvider initializes at app boot, before any siteConfig admin theme
// overrides land. The Clerk theme is therefore independent of admin theme
// changes — a deliberate trade-off for now (making Clerk reactive to
// admin theme changes would require a state-driven ClerkProvider wrapper,
// out of scope for this legibility pass).
const clerkAppearance = {
    variables: {
        // Brand pink-dark. Used by Clerk for focus rings, inline action
        // links (e.g. "Forgot password?" inside the form), accent borders,
        // and any element that doesn't have an explicit override below.
        // ~7:1 contrast on white — passes WCAG AAA Normal text.
        colorPrimary: '#a02a52',
        // White text on the now-saturated primary background. Both the
        // primary button and any colored-pill UI Clerk renders use this.
        colorTextOnPrimaryBackground: '#ffffff',
        colorBackground: '#ffffff',
        colorText: '#2d2926',
        // Darker secondary gray — passes WCAG AA Normal cleanly. Was #8a837d
        // (~4.0:1, borderline failing). Now ~5.5:1.
        colorTextSecondary: '#6b6660',
        colorInputBackground: '#f5f0eb',
        colorInputText: '#2d2926',
        borderRadius: '14px',
    },
    elements: {
        card: {
            boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
            borderRadius: '24px',
            border: '1px solid rgba(0,0,0,0.04)',
        },
        headerTitle: {
            fontFamily: 'var(--mom-font-serif)',
            fontStyle: 'italic',
            fontWeight: 700,
        },
        headerSubtitle: {
            fontFamily: 'var(--mom-font-sans)',
        },
        formButtonPrimary: {
            // Brand pink gradient — matches .mom-btn-primary on the landing
            // page (Sign Up / Sign In CTAs), the locked-video Subscribe
            // button, and the "See Workouts" dashboard CTA. White text reads
            // at 5:1+ contrast across the whole gradient (5.07:1 on the
            // lighter end #c33764, 7+:1 on the darker end #a02a52).
            background: 'linear-gradient(135deg, #c33764, #a02a52)',
            color: '#ffffff',
            borderRadius: '14px',
            fontWeight: 600,
            fontFamily: 'var(--mom-font-sans)',
            textTransform: 'none',
            // Shadow tint matched to the brand pink.
            boxShadow: '0 4px 14px rgba(195, 55, 100, 0.35)',
        },
        formFieldInput: {
            borderRadius: '12px',
            fontFamily: 'var(--mom-font-sans)',
            border: '1px solid #ede6de',
        },
        formFieldLabel: {
            fontFamily: 'var(--mom-font-sans)',
            fontWeight: 500,
            // Explicit charcoal — labels are critical wayfinding for older
            // eyes ("which field is this?"). High contrast over the previous
            // implicit secondary-gray inheritance.
            color: '#2d2926',
        },
        // The most-impactful single fix: the link at the bottom of every
        // sign-in/sign-up/forgot-password screen ("Don't have an account?
        // Sign up", etc.). Was #f0d6d4 — visually invisible on white. Now
        // brand pink-dark + underline for both color contrast AND the
        // semantic "this is a clickable link" cue.
        footerActionLink: {
            color: '#a02a52',
            fontFamily: 'var(--mom-font-sans)',
            fontWeight: 600,
            textDecoration: 'underline',
        },
        socialButtonsBlockButton: {
            borderRadius: '12px',
            border: '1px solid #ede6de',
        },
        dividerLine: { background: '#ede6de' },
        dividerText: { fontFamily: 'var(--mom-font-sans)' },
        identityPreview: { borderRadius: '12px' },
        alert: { borderRadius: '12px' },
    },
};

const root = ReactDOM.createRoot(document.getElementById('root'));

if (clerkPubKey) {
    root.render(
        <React.StrictMode>
            <HelmetProvider>
                <ClerkProvider publishableKey={clerkPubKey} appearance={clerkAppearance}>
                    <BrowserRouter><App /></BrowserRouter>
                </ClerkProvider>
            </HelmetProvider>
        </React.StrictMode>
    );
} else {
    root.render(
        <React.StrictMode>
            <HelmetProvider>
                <BrowserRouter><App demoMode={true} /></BrowserRouter>
            </HelmetProvider>
        </React.StrictMode>
    );
}